import { Skeleton } from '../primitives';
import { EditorPropertyBar } from '../primitives/EditorPropertyBar';
import { WorkItemCanvasBuiltInPills } from './WorkItemCanvasBuiltInPills';
import { WorkItemCanvasCustomPill } from './WorkItemCanvasCustomPill';
import { useWorkItemCanvasPropertyBar } from './useWorkItemCanvasPropertyBar';
import type { Database } from '../../types/database';

type WorkItemType = Database['public']['Enums']['work_item_type'];

export interface WorkItemCanvasPropertyBarProps {
  workItemId: string;
  orgId: string;
  workItemType: WorkItemType;
  workItemStatus: string;
  workItemDueDate: string | null;
  workItemStartDate: string | null;
  workItemEndDate: string | null;
  parentWorkItemId: string | null;
  parentName: string | null;
  workItemProjectId: string | null;
  onStatusChange: (status: string) => void;
  onDueDateChange: (next: string | null) => void;
  onStartDateChange: (next: string | null) => void;
  onEndDateChange: (next: string | null) => void;
  onParentChange: (next: string | null) => void;
  onProjectChange: (next: string | null) => void;
  onError: (message: string) => void;
  onActivityChange?: () => void;
  editable: boolean;
  testIdPrefix?: string;
}

export function WorkItemCanvasPropertyBar({
  workItemId,
  orgId,
  workItemType,
  workItemStatus,
  workItemDueDate,
  workItemStartDate,
  workItemEndDate,
  parentWorkItemId,
  parentName,
  workItemProjectId,
  onStatusChange,
  onDueDateChange,
  onStartDateChange,
  onEndDateChange,
  onParentChange,
  onProjectChange,
  onError,
  onActivityChange,
  editable,
  testIdPrefix = 'work-item-detail-property-bar'
}: WorkItemCanvasPropertyBarProps) {
  const {
    load,
    statusStages,
    epicOptions,
    projectOptions,
    dateError,
    commitValue,
    commitStatus,
    commitDue,
    commitStart,
    commitEnd,
    commitParent,
    commitProject
  } = useWorkItemCanvasPropertyBar({
    workItemId,
    orgId,
    workItemType,
    workItemStatus,
    workItemDueDate,
    workItemStartDate,
    workItemEndDate,
    parentWorkItemId,
    workItemProjectId,
    onStatusChange,
    onDueDateChange,
    onStartDateChange,
    onEndDateChange,
    onParentChange,
    onProjectChange,
    onError,
    onActivityChange
  });

  return (
    <div
      className="wid-property-bar-canvas pn-editor-property-bar-host"
      aria-label="Work item properties"
    >
      <EditorPropertyBar testIdPrefix={testIdPrefix} layout="scroll">
        <WorkItemCanvasBuiltInPills
          workItemType={workItemType}
          workItemStatus={workItemStatus}
          workItemDueDate={workItemDueDate}
          workItemStartDate={workItemStartDate}
          workItemEndDate={workItemEndDate}
          parentWorkItemId={parentWorkItemId}
          parentName={parentName}
          statusStages={statusStages}
          epicOptions={epicOptions}
          workItemProjectId={workItemProjectId}
          projectOptions={projectOptions}
          dateError={dateError}
          editable={editable}
          testIdPrefix={testIdPrefix}
          commitStatus={commitStatus}
          commitDue={commitDue}
          commitStart={commitStart}
          commitEnd={commitEnd}
          commitParent={commitParent}
          commitProject={commitProject}
        />

        {load.kind === 'error' ? (
          <div className="wid-properties-error" role="alert">
            {load.message}
          </div>
        ) : null}
        {load.kind === 'loading' ? (
          <>
            <Skeleton width={120} height={28} />
            <Skeleton width={120} height={28} />
          </>
        ) : null}
        {load.kind === 'ready'
          ? load.defs.map((def) => (
              <WorkItemCanvasCustomPill
                key={def.id}
                def={def}
                value={load.values.get(def.id) ?? null}
                editable={editable}
                testIdPrefix={testIdPrefix}
                onCommit={commitValue}
              />
            ))
          : null}
      </EditorPropertyBar>
    </div>
  );
}
